"use client";

import React, { useState, useEffect, useCallback } from "react";
import { motion } from "motion/react";
import {
	InputOTP,
	InputOTPGroup,
	InputOTPSlot,
} from "@/components/ui/input-otp";
import { toast } from "sonner";
import {
	getCooldownRemainingSeconds,
	recordOtpSent,
	getStoredState,
	type OTPResendScope,
} from "@/lib/otp-resend-cooldown";

const INITIAL_COOLDOWN_SECONDS = 60;

interface OTPInputProps {
	email: string;
	onSubmit: (otp: string) => void;
	onResend?: () => Promise<void>;
	title?: string;
	description?: string;
	errorMessage?: string;
	/** When set, enforces exponential resend cooldown (persisted in sessionStorage across reloads). */
	cooldownScope?: OTPResendScope;
}

function getInitialTimerAndCanResend(
	cooldownScope: OTPResendScope | undefined,
	email: string
): { timer: number; canResend: boolean } {
	if (!cooldownScope || !email) {
		return { timer: INITIAL_COOLDOWN_SECONDS, canResend: false };
	}
	const remaining = getCooldownRemainingSeconds(cooldownScope, email);
	if (remaining > 0) {
		return { timer: remaining, canResend: false };
	}
	const state = getStoredState(cooldownScope, email);
	// First time on OTP page (no prior resend): wait INITIAL_COOLDOWN_SECONDS before first resend
	if (!state) {
		return { timer: INITIAL_COOLDOWN_SECONDS, canResend: false };
	}
	return { timer: 0, canResend: true };
}

export default function OTPInput({
	email,
	onSubmit,
	onResend,
	title = "Verify OTP",
	description,
	errorMessage,
	cooldownScope,
}: OTPInputProps) {
	const initial = getInitialTimerAndCanResend(cooldownScope, email);
	const [otp, setOtp] = useState("");
	const [resendTimer, setResendTimer] = useState(initial.timer);
	const [canResend, setCanResend] = useState(initial.canResend);
	const [submitting, setSubmitting] = useState(false);

	// Re-sync from storage when email or scope changes (e.g. after reload)
	useEffect(() => {
		const { timer, canResend: allow } = getInitialTimerAndCanResend(
			cooldownScope,
			email
		);
		setResendTimer(timer);
		setCanResend(allow);
	}, [cooldownScope, email]);

	useEffect(() => {
		if (resendTimer > 0) {
			const timer = setTimeout(
				() => setResendTimer(resendTimer - 1),
				1000
			);
			return () => clearTimeout(timer);
		} else {
			setCanResend(true);
		}
	}, [resendTimer]);

	const handleResend = useCallback(async () => {
		if (!onResend) return;
		if (!canResend) return;
		if (cooldownScope && email) {
			const remaining = getCooldownRemainingSeconds(cooldownScope, email);
			if (remaining > 0) {
				setResendTimer(remaining);
				setCanResend(false);
				return;
			}
		}
		try {
			await onResend();
			toast.success("OTP resent successfully!", {
				description: `A new code has been sent to ${email}`,
			});
			if (cooldownScope && email) {
				const nextCooldown = recordOtpSent(cooldownScope, email);
				setResendTimer(nextCooldown);
			} else {
				setResendTimer(INITIAL_COOLDOWN_SECONDS);
			}
			setCanResend(false);
			setOtp("");
		} catch (error: unknown) {
			const errorMessage =
				error instanceof Error
					? error.message
					: "Failed to resend OTP. Please try again.";
			toast.error(errorMessage);
		}
	}, [canResend, cooldownScope, email, onResend]);

	// Clear OTP when a new error message appears (user needs to try again)
	useEffect(() => {
		if (errorMessage && errorMessage.trim() !== "") {
			setOtp("");
		}
	}, [errorMessage]);

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		if (otp.length === 6 && !submitting) {
			setSubmitting(true);
			try {
				await onSubmit(otp);
			} finally {
				setSubmitting(false);
			}
		}
	};

	return (
		<motion.div
			initial={{ opacity: 0, y: 20 }}
			animate={{ opacity: 1, y: 0 }}
			exit={{ opacity: 0, y: -20 }}
			transition={{ duration: 0.3 }}
			className="content-stretch flex flex-col gap-[2rem] items-start relative shrink-0 w-[25rem]"
		>
			<div className="content-stretch flex flex-col gap-[0.5rem] items-start not-italic relative shrink-0 w-full">
				<p className="font-['Inter:Semi_Bold',sans-serif] font-semibold leading-[2.375rem] relative shrink-0 text-[#101828] text-[1.875rem] w-full">
					{title}
				</p>
				<p className="font-['Inter:Regular',sans-serif] font-normal leading-[1.5rem] relative shrink-0 text-[#475467] text-[1rem] w-full">
					{description || `Enter the 6-digit code sent to ${email}`}
				</p>
			</div>
			{errorMessage && (
				<motion.div
					initial={{ opacity: 0, y: -10 }}
					animate={{ opacity: 1, y: 0 }}
					className="w-full p-3 bg-red-50 border border-red-200 rounded-lg"
				>
					<p className="text-sm text-red-600 font-medium">
						{errorMessage}
					</p>
				</motion.div>
			)}
			<form
				onSubmit={handleSubmit}
				className="content-stretch flex flex-col gap-[1.5rem] items-center relative rounded-[0.75rem] shrink-0 w-full"
			>
				<div className="content-stretch flex flex-col gap-[1rem] items-start relative shrink-0 w-full">
					<div className="content-stretch flex flex-col gap-[0.375rem] items-start relative shrink-0 w-full">
						<div className="content-stretch flex gap-[0.375rem] items-start relative shrink-0 w-full">
							<p className="basis-0 font-['Inter:Medium',sans-serif] font-medium grow leading-[1.25rem] min-h-px min-w-px not-italic relative shrink-0 text-[#12301b] text-[0.875rem]">
								OTP Code*
							</p>
						</div>
						<div className="flex justify-center w-full py-4">
							<InputOTP
								maxLength={6}
								value={otp}
								onChange={setOtp}
								disabled={submitting}
								onKeyDown={(e) => {
									if (e.key === "Enter") {
										e.preventDefault();
										if (otp.length === 6 && !submitting) {
											onSubmit(otp);
										}
									}
								}}
							>
								<InputOTPGroup>
									<InputOTPSlot
										index={0}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
									<InputOTPSlot
										index={1}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
									<InputOTPSlot
										index={2}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
									<InputOTPSlot
										index={3}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
									<InputOTPSlot
										index={4}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
									<InputOTPSlot
										index={5}
										className="h-[3.25rem] w-[3.25rem] text-[1.25rem] border-[#dde3e9]"
									/>
								</InputOTPGroup>
							</InputOTP>
						</div>
						<div className="content-stretch flex gap-[0.375rem] items-start relative shrink-0 w-full">
							<p className="basis-0 font-['Inter:Medium',sans-serif] font-medium grow leading-[1.25rem] min-h-px min-w-px not-italic relative shrink-0 text-[#8c99a7] text-[0.75rem]">
								Didn&apos;t receive code?{" "}
								{canResend && onResend ? (
									<button
										type="button"
										onClick={handleResend}
										disabled={submitting}
										className="text-[#2769af] hover:underline cursor-pointer disabled:opacity-50"
									>
										Resend
									</button>
								) : (
									<span className="text-[#8c99a7]">
										Resend in {resendTimer}s
									</span>
								)}
							</p>
						</div>
					</div>
				</div>
				<div className="content-stretch flex flex-col gap-[1rem] items-start relative shrink-0 w-full">
					<button
						type="submit"
						disabled={otp.length !== 6 || submitting}
						className="bg-[#cbf7a6] h-[3.25rem] relative rounded-[2.25rem] shrink-0 w-full transition-transform hover:scale-[1.02] active:scale-[0.98] cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
					>
						<div
							aria-hidden="true"
							className="absolute border border-[#12301b] border-solid inset-0 pointer-events-none rounded-[2.25rem]"
						/>
						<div className="flex flex-row items-center justify-center size-full">
							<div className="box-border content-stretch flex gap-[0.5rem] h-[3.25rem] items-center justify-center px-[1.5rem] py-[1rem] relative w-full">
								<p className="font-['Inter:Extra_Bold',sans-serif] font-extrabold leading-[1.5rem] not-italic relative shrink-0 text-[#12301b] text-[1rem] text-nowrap tracking-[0.01rem] whitespace-pre">
									{submitting ? "Verifying..." : "Verify OTP"}
								</p>
							</div>
						</div>
					</button>
				</div>
			</form>
		</motion.div>
	);
}
